/// <reference types="react" />
import React from "react";

type Level = "High" | "Medium" | "Low";
type Props = {
  score: number | null | undefined;   // 0..1 expected
  thresholds?: { high: number; medium: number }; // defaults: 0.82 / 0.68
  showPercent?: boolean;               // default false
  className?: string;
};

export function ConfidencePill({
  score,
  thresholds = { high: 0.82, medium: 0.68 },
  showPercent = false,
  className = "",
}: Props) {
  // Guard + clamp
  const s = Number.isFinite(score as number) ? Math.min(1, Math.max(0, Number(score))) : 0;

  const level: Level =
    s >= thresholds.high ? "High" : s >= thresholds.medium ? "Medium" : "Low";

  const palette = {
    High:  "bg-emerald-500/15 text-emerald-300",
    Medium:"bg-amber-500/15  text-amber-300",
    Low:   "bg-rose-500/15   text-rose-300",
  } as const;

  const pct = Math.round(s * 100);
  // Screen-reader friendly description
  const ariaText = `Confidence ${level} (${pct} percent)`;

  return (
    <span
      role="meter"
      aria-label="Confidence"
      aria-valuemin={0}
      aria-valuemax={1}
      aria-valuenow={Number.isFinite(s) ? s : undefined}
      aria-valuetext={ariaText}
      data-state={level.toLowerCase()} // handy for testing/styling
      className={[
        "inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs",
        "transition-colors", // subtle polish
        palette[level],
        className,
      ].join(" ")}
      // Keep title for mouse users; SRs rely on the aria-* above
      title={`${pct}%`}
    >
      <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-current" />
      <span>{level}</span>
      {showPercent && (
        <span className="ml-1 opacity-80 tabular-nums">({pct}%)</span>
      )}
    </span>
  );
}
